import { Component, h, Host, Prop, Element, Event } from '@stencil/core';
import warningIcon from '../../../assets/notification_alert.svg';
import informationIcon from '../../../assets/notification_information.svg';
import errorIcon from '../../../assets/notification_error.svg';
import iconClose from '../../../assets/icon-close.svg';
import { resizeSVGsForEdge } from '../../../global/utils/utils';
/**
 *@slot default - The content placed in this slot is intended to replace the message and link properties and will appear just below them.
 *@slot message - (optional) The content placed in this slot is authorable via AEM RTE, it could be any semantic HMTL tag
 *@slot call-to-action - (optional) The content placed in this slot should be agl-ds-button, and or agl-ds-link
 */
export class NotificationCardComponent {
  constructor() {
    /**
     * The type of  Notification
     */
    this.type = 'information';
    /**
     * The heading for the navigation card
     */
    this.heading = '';
    /**
     * The message for the navigation card
     */
    this.message = '';
    /**
     * The type for action button
     */
    this.buttonType = 'secondary';
    /**
     * The destination url for the notification link click
     */
    this.linkHref = '';
    /**
     * Determines if the destination is opened in a new window
     */
    this.linkOpenNewWindow = true;
    /**
     * Determines if the icon needs to be displayed
     */
    this.showIcon = true;
    /**
     * Determines if the close icon needs to be displayed
     */
    this.showCloseIcon = false;
    /**
     * Get icon based on Notification type
     */
    this.getNotificationIcon = () => {
      switch (this.type) {
        case 'warning':
          return warningIcon;
        case 'error':
          return errorIcon;
        case 'information':
          return informationIcon;
        default:
          return null;
      }
    };
    /**
     * Handle click event of button
     */
    this.handleButtonClick = () => {
      if (this.buttonClick) {
        this.buttonClick.emit();
      }
    };
    /**
     * Handle click event of close icon
     */
    this.handleCloseButtonClick = () => {
      if (this.closeIconClick) {
        this.closeIconClick.emit();
      }
    };
  }
  componentWillLoad() {
    this.hasMessageSlot = !!this.hostElement.querySelector('[slot="message"]');
    this.hasCtaSlot = !!this.hostElement.querySelector('[slot="call-to-action"]');
  }
  componentDidLoad() {
    resizeSVGsForEdge(this.svgSpan, '24', '24', '32', '32');
  }
  render() {
    if (!['error', 'information', 'warning'].includes(this.type)) {
      throw new Error(this.type + ' is not a valid NotificationType');
    }
    return (h(Host, null,
      h("div", { class: {
          'notification-container': true,
          ['notification-container--' + this.type]: true,
          'notification-container--with-heading': !!this.heading
        }, tabindex: this.type === 'error' ? '-1' : '0' },
        this.showIcon && this.getNotificationIcon() && (h("div", { class: { 'notification-container__icon': true } },
          h("span", { ref: (el) => (this.svgSpan = el), "aria-hidden": "true", innerHTML: this.getNotificationIcon() }))),
        h("div", { class: { 'notification-container__content': true } },
          !!this.heading && (h("div", { class: { 'notification-container__heading': true } },
            h("agl-ds-h4", null, this.heading))),
          h("div", { class: {
              'notification-container--warning--message': this.type === 'warning' && !this.heading,
              'notification-container--error--message': this.type === 'error' && !this.heading
            } },
            (!!this.message || !!this.linkText) && (h("agl-ds-p", { "bottom-margin": "none" },
              this.message,
              "\u00A0",
              !!this.linkText && (h("agl-ds-link", { srContext: this.heading + this.message, href: this.linkHref, openNewWindow: this.linkOpenNewWindow }, this.linkText)))),
            h("slot", null),
            this.hasMessageSlot && h("slot", { name: "message" })),
          !!this.buttonText && (h("div", { class: { 'notification-container__button': true } },
            h("agl-ds-button", { onClick: this.handleButtonClick, type: this.buttonType }, this.buttonText))),
          this.hasCtaSlot && h("slot", { name: "call-to-action" })),
        !!this.imagePath && (h("div", { class: { 'notification-container__image': true } },
          h("img", { "aria-hidden": "true", src: this.imagePath }))),
        this.showCloseIcon && (h("button", { class: {
            'notification-container__close-button': true
          }, "aria-label": "close the notification", onClick: () => {
            this.handleCloseButtonClick();
          } },
          h("agl-ds-icon", { icon: iconClose, size: "xxs", "alt-text": "close the notification" }))))));
  }
  static get is() { return "agl-ds-notification"; }
  static get encapsulation() { return "shadow"; }
  static get originalStyleUrls() { return {
    "$": ["notification-card.scss"]
  }; }
  static get styleUrls() { return {
    "$": ["notification-card.css"]
  }; }
  static get properties() { return {
    "type": {
      "type": "string",
      "mutable": false,
      "complexType": {
        "original": "NotificationType",
        "resolved": "\"error\" | \"information\" | \"warning\"",
        "references": {
          "NotificationType": {
            "location": "import",
            "path": "./notification-card.types"
          }
        }
      },
      "required": false,
      "optional": false,
      "docs": {
        "tags": [],
        "text": "The type of  Notification"
      },
      "attribute": "type",
      "reflect": false,
      "defaultValue": "'information'"
    },
    "imagePath": {
      "type": "string",
      "mutable": false,
      "complexType": {
        "original": "string",
        "resolved": "string",
        "references": {}
      },
      "required": false,
      "optional": false,
      "docs": {
        "tags": [],
        "text": "The path of the image to be displayed on the banner"
      },
      "attribute": "image-path",
      "reflect": false
    },
    "heading": {
      "type": "string",
      "mutable": false,
      "complexType": {
        "original": "string",
        "resolved": "string",
        "references": {}
      },
      "required": false,
      "optional": false,
      "docs": {
        "tags": [],
        "text": "The heading for the navigation card"
      },
      "attribute": "heading",
      "reflect": false,
      "defaultValue": "''"
    },
    "message": {
      "type": "string",
      "mutable": false,
      "complexType": {
        "original": "string",
        "resolved": "string",
        "references": {}
      },
      "required": false,
      "optional": false,
      "docs": {
        "tags": [],
        "text": "The message for the navigation card"
      },
      "attribute": "message",
      "reflect": false,
      "defaultValue": "''"
    },
    "buttonText": {
      "type": "string",
      "mutable": false,
      "complexType": {
        "original": "string",
        "resolved": "string",
        "references": {}
      },
      "required": false,
      "optional": false,
      "docs": {
        "tags": [],
        "text": "The text for action button"
      },
      "attribute": "button-text",
      "reflect": false
    },
    "buttonType": {
      "type": "string",
      "mutable": false,
      "complexType": {
        "original": "ButtonType",
        "resolved": "\"primary\" | \"secondary\" | \"tertiary\"",
        "references": {
          "ButtonType": {
            "location": "import",
            "path": "../../core/button/button.types"
          }
        }
      },
      "required": false,
      "optional": false,
      "docs": {
        "tags": [],
        "text": "The type for action button"
      },
      "attribute": "button-type",
      "reflect": false,
      "defaultValue": "'secondary'"
    },
    "linkText": {
      "type": "string",
      "mutable": false,
      "complexType": {
        "original": "string",
        "resolved": "string",
        "references": {}
      },
      "required": false,
      "optional": false,
      "docs": {
        "tags": [],
        "text": "The text for link"
      },
      "attribute": "link-text",
      "reflect": false
    },
    "linkHref": {
      "type": "string",
      "mutable": false,
      "complexType": {
        "original": "string",
        "resolved": "string",
        "references": {}
      },
      "required": false,
      "optional": false,
      "docs": {
        "tags": [],
        "text": "The destination url for the notification link click"
      },
      "attribute": "link-href",
      "reflect": false,
      "defaultValue": "''"
    },
    "linkOpenNewWindow": {
      "type": "boolean",
      "mutable": false,
      "complexType": {
        "original": "boolean",
        "resolved": "boolean",
        "references": {}
      },
      "required": false,
      "optional": false,
      "docs": {
        "tags": [],
        "text": "Determines if the destination is opened in a new window"
      },
      "attribute": "link-open-new-window",
      "reflect": false,
      "defaultValue": "true"
    },
    "showIcon": {
      "type": "boolean",
      "mutable": false,
      "complexType": {
        "original": "boolean",
        "resolved": "boolean",
        "references": {}
      },
      "required": false,
      "optional": false,
      "docs": {
        "tags": [],
        "text": "Determines if the icon needs to be displayed"
      },
      "attribute": "show-icon",
      "reflect": false,
      "defaultValue": "true"
    },
    "showCloseIcon": {
      "type": "boolean",
      "mutable": false,
      "complexType": {
        "original": "boolean",
        "resolved": "boolean",
        "references": {}
      },
      "required": false,
      "optional": false,
      "docs": {
        "tags": [],
        "text": "Determines if the close icon needs to be displayed"
      },
      "attribute": "show-close-icon",
      "reflect": false,
      "defaultValue": "false"
    }
  }; }
  static get events() { return [{
      "method": "buttonClick",
      "name": "buttonClick",
      "bubbles": true,
      "cancelable": true,
      "composed": true,
      "docs": {
        "tags": [],
        "text": "Action Button Click event"
      },
      "complexType": {
        "original": "any",
        "resolved": "any",
        "references": {}
      }
    }, {
      "method": "closeIconClick",
      "name": "closeIconClick",
      "bubbles": true,
      "cancelable": true,
      "composed": true,
      "docs": {
        "tags": [],
        "text": "Fires when close Icon is clicked"
      },
      "complexType": {
        "original": "void",
        "resolved": "void",
        "references": {}
      }
    }]; }
  static get elementRef() { return "hostElement"; }
}
